import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa6';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { IconType } from 'react-icons';
import { toast } from 'react-toastify';
import { FiCheckCircle } from 'react-icons/fi';
import { SubscriptionType } from '../../../types/misc';
import {
  baseSubscriptionPrices,
  variableSubscriptionPrices,
  subscriptionFeatures,
  isSubscriptionGreaterThanOrEqual,
  isSubscriptionGreaterThan,
} from '../../../utils/subscriptionUtils';
import CheckoutForm from '../../components/forms/CheckoutForm';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import useActiveSubscription from '../../../hooks/business/useActiveSubscription';
import Button from '../../components/buttons/Button';
import { stripeAppearance } from '../../../constants';
import { subscriptionService } from '../../../services/subscriptionService';
import TrialBanner from '../../components/misc/TrialBanner';

const STRIPE_KEY: string = process.env.REACT_APP_STRIPE_KEY as string;

const stripe = loadStripe(STRIPE_KEY);

export default function UpgradeSubscriptionPage(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: '' });
  const location = useLocation();
  const subscriptionType = location.state?.subscriptionType as SubscriptionType | undefined;
  const business = useSelectedBusiness();
  const activeSubscription = useActiveSubscription();
  const navigate = useNavigate();
  const [changePaymentMode, setChangePaymentMode] = useState<boolean>(location.state?.changePaymentMode as boolean | false);
  const [userCount, setUserCount] = useState(0);

  const isOnTrial = business?.trialEndDate && new Date(business.trialEndDate) > new Date();

  useEffect(() => {
    const fetchUserCount = async () => {
      const count = await subscriptionService.getSubscriptionUsage();
      setUserCount(count);
    };
    fetchUserCount();
  }, [business]);

  const calculatePricing = (type: SubscriptionType, memberCount: number) => {
    const basePrice = baseSubscriptionPrices[type];
    const variablePrice = variableSubscriptionPrices[type];
    const extraMembers = Math.max(0, memberCount - 25);
    return basePrice + (extraMembers * variablePrice);
  };

  function shouldShowTrialBanner(): boolean {
    if (!business?.trialEndDate || !business?.trialType || business?.subscription?.subscriptionType) return false;
    const now = new Date();
    const trialEnd = new Date(business.trialEndDate);
    const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return trialEnd.getTime() > now.getTime() && daysLeft < 31;
  }

  if (!subscriptionType || changePaymentMode === undefined) {
    return <></>;
  }

  return (
    <>
      {business && business.trialEndDate && business.trialType && shouldShowTrialBanner() && (
        <TrialBanner 
          trialEndDate={new Date(business.trialEndDate)} 
          plan={business.trialType}
          showPaymentSetup={false}
          onPaymentSetup={() => navigate('/subscriptions/upgrade', { state: { changePaymentMode: false, subscriptionType: activeSubscription } })}
        />
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="rounded-3xl bg-white shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-primary-900">
                {t('page.subscriptions.upgrade.title')}
              </h1>
            </div>

            <div className="mb-8">
              {!changePaymentMode && (
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className='rounded-2xl p-4 bg-secondary-200'>
                      <h3 className="text-lg font-semibold text-primary-900">
                        {activeSubscription} {isOnTrial ? `(${t('page.settings.manageSubscription.freeTrial')})` : ''}
                      </h3>
                      <p className="text-sm text-primary-900 opacity-90">
                        {t('page.subscriptions.upgrade.current')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <FaArrowRight className="h-6 w-6 text-primary-900" />
                  </div>
                  
                  <div className="flex-1">
                    <div className='rounded-2xl p-4 bg-secondary-200'>
                      <h3 className="text-lg font-semibold text-primary-900">
                        {subscriptionType}
                      </h3>
                      <p className="text-sm text-primary-900 opacity-90">
                        {t('page.subscriptions.upgrade.upgrading')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-secondary-200 p-6 mb-8">
              <h3 className="text-lg font-semibold text-primary-900 mb-4">
                {t('page.subscriptions.upgrade.pricing')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-primary-900">
                    {t('page.subscriptions.upgrade.basePrice')}
                  </span>
                  <span className="font-semibold text-primary-900">
                    €{baseSubscriptionPrices[subscriptionType].toFixed(2)}/{t('general.month')}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-primary-900">
                    {t('page.subscriptions.upgrade.extraMembers', { count: userCount > 25 ? userCount - 25 : 0 })}
                  </span>
                  <span className="font-semibold text-primary-900">
                    €{((userCount > 25 ? userCount - 25 : 0) * variableSubscriptionPrices[subscriptionType]).toFixed(2)}/{t('general.month')}
                  </span>
                </div>
                
                <div className="border-t border-primary-300 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-primary-900">
                      {t('page.subscriptions.upgrade.total')}
                    </span>
                    <span className="text-xl font-bold text-primary-900">
                      €{calculatePricing(subscriptionType, userCount).toFixed(2)}/{t('general.month')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-primary-900 mb-4">
                {t('page.subscriptions.upgrade.benefits')}
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {subscriptionFeatures
                  .filter((f) => f[subscriptionType])
                  .map((feature) => (
                    <div key={feature.i18nKey} className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <FiCheckCircle className="h-5 w-5 text-primary-300" />
                      </div>
                      <span className="text-primary-900">
                        {t(`page.settings.subscriptions.${feature.i18nKey}`)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl bg-white shadow-lg p-8">
            <h2 className="text-2xl font-semibold text-primary-900 mb-6">
              {isOnTrial 
                ? t('page.subscriptions.upgrade.setupPayment') 
                : t('page.subscriptions.upgrade.paymentDetails')
              }
            </h2>

            {isOnTrial && (
              <ConditionListItem
                icon={FiCheckCircle}
                text={t('page.subscriptions.upgrade.noChargeUntilTrial')}
              />
            )}
            {subscriptionType !== SubscriptionType.FREE && (
              <>
                <ConditionListItem
                  icon={FiCheckCircle}
                  text={t('page.subscriptions.upgrade.subscriptionDetails')}
                />
                <ConditionListItem
                  icon={FiCheckCircle}
                  text={t('page.subscriptions.upgrade.cancelPolicy')}
                />
              </>
            )}

            <PaymentForm
              subscriptionType={subscriptionType}
              activeSubscription={activeSubscription}
              isOnTrial={!!isOnTrial || undefined}
              changePaymentMode={changePaymentMode}
              setChangePaymentMode={setChangePaymentMode}
            />

            
          </div>
        </div>
      </div>
    </>
  );
}

function ConditionListItem({ icon: Icon, text }: { icon: IconType; text: string }): JSX.Element {
  return (
    <li className="flex items-center gap-1.5">
      <Icon className="mt-0.5 shrink-0 self-start text-xl" />
      {text}
    </li>
  );
}
type PaymentFormProps = {
  subscriptionType: SubscriptionType;
  activeSubscription: SubscriptionType;
  isOnTrial: boolean | undefined;
  changePaymentMode: boolean;
  setChangePaymentMode: React.Dispatch<React.SetStateAction<boolean>>;
};
function PaymentForm({
  subscriptionType,
  activeSubscription,
  isOnTrial,
  changePaymentMode,
  setChangePaymentMode,
}: PaymentFormProps): JSX.Element {
  const { t } = useTranslation();
  const business = useSelectedBusiness();
  const navigate = useNavigate();
  const [disabledForm, setDisabledForm] = useState<boolean>(false);
  const [agreedWithTerms, setAgreedWithTerms] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);

  const isUpgrade = isSubscriptionGreaterThan(subscriptionType, activeSubscription);
  const isDowngrade = isSubscriptionGreaterThanOrEqual(activeSubscription, subscriptionType);
  const isCancel = subscriptionType === SubscriptionType.FREE;
  const needsPaymentSetup = (subscriptionType !== SubscriptionType.FREE && activeSubscription === SubscriptionType.FREE) || (isOnTrial && !isDowngrade) || changePaymentMode;

  useEffect(() => {
    if (needsPaymentSetup) {
      subscriptionService.prepareSubscription(subscriptionType).then(({ status, clientSecret: cs, customerId: cid }) => {
          if (status === 'subscription_already_active') setChangePaymentMode(true);
          setClientSecret(cs);
          setCustomerId(cid);
        });
    }
  }, [business]);

  const options = {
    appearance: { ...stripeAppearance },
    clientSecret,
  } as StripeElementsOptions;

  const changeSubscription = async () => {
    if (subscriptionType && business && business.businessId !== undefined) {
      subscriptionService.changeSubscription(subscriptionType)
        .then(() => navigate('/subscriptions/manage'))
        .catch(() => toast.error(t('page.subscriptions.upgrade.error')));
    }
  };

  const cancelSubscription = async () => {
    subscriptionService.cancelSubscription()
      .then(() => navigate('/subscriptions/manage'))
      .catch(() => toast.error(t('page.subscriptions.upgrade.error')));
  };

  return (
    <div className="space-y-6">
      {needsPaymentSetup ? (
        clientSecret && stripe && (
          <Elements options={options} stripe={stripe}>
            {!changePaymentMode ? (
              <CheckoutForm
                clientSecret={clientSecret}
                disabled={disabledForm}
                setDisabled={setDisabledForm}
                intentType="setup"
                onSubmit={() => {}}
                redirectQueryParams={`?customerId=${customerId}&status=loading&type=setup`}
              />
            ) : (
              <CheckoutForm
                clientSecret={clientSecret}
                disabled={disabledForm}
                setDisabled={setDisabledForm}
                intentType="setupChange"
                onSubmit={() => {}}
                redirectQueryParams={`?customerId=${customerId}&status=loading&type=setup`}
              />
            )}
          </Elements>
        )
      ) : isUpgrade ? (
        <>
          <TermsCheckbox label={t('page.subscriptions.upgrade.acceptPayment')} agreedWithTerms={agreedWithTerms} setAgreedWithTerms={setAgreedWithTerms} />
          <ActionButton
            onClick={changeSubscription}
            label={t('page.subscriptions.upgrade.confirmUpgrade')}
            disabled={!agreedWithTerms}
          />
        </>
      ) : isDowngrade ? (
        <>
          <TermsCheckbox label={t('page.subscriptions.upgrade.accept')} agreedWithTerms={agreedWithTerms} setAgreedWithTerms={setAgreedWithTerms} />
          <ActionButton
            onClick={isCancel ? cancelSubscription : changeSubscription}
            label={
              isCancel || changePaymentMode
                ? t('page.subscriptions.upgrade.confirmChange')
                : t('page.subscriptions.upgrade.setupPaymentMethod')
            }
            disabled={!agreedWithTerms}
          />
        </>
      ) : null}
    </div>
  );
}

function TermsCheckbox({ label, agreedWithTerms, setAgreedWithTerms }: { label: string; agreedWithTerms: boolean; setAgreedWithTerms: React.Dispatch<React.SetStateAction<boolean>> }) {
  return (
    <div className="rounded-2xl bg-secondary-200 p-4">
      <div className="flex items-center gap-3">
        <input
          id="terms"
          type="checkbox"
          checked={agreedWithTerms}
          onChange={() => setAgreedWithTerms(prev => !prev)}
          className="h-5 w-5 rounded border-2 border-primary-300 text-primary-300 focus:ring-primary-300"
        />
        <label htmlFor="terms" className="text-primary-900 font-medium cursor-pointer flex-1">
          {label}
        </label>
      </div>
    </div>
  );
}

function ActionButton({ onClick, label, disabled }: { onClick: () => void; label: string; disabled: boolean }) {
  return (
    <Button
      disabled={disabled}
      onClick={onClick}
      variant="primary"
      className="w-full justify-center py-4 text-lg font-semibold"
    >
      {label}
    </Button>
  );
}